import { keepPreviousData, MutationCache, QueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { QUICK_START_QUERY_KEY } from '@/features/setup-tours/hooks/useQuickStartGuide';
import { SETUP_STATUS_QUERY_KEY_PREFIX } from '@/features/setup-tours/hooks/useSetupStatus';

/**
 * Refetches the sidebar progress: the Tours and the Setup bars. The MutationCache calls it
 * after every mutation; a direct `apiClient` write that can change a setup step calls it too.
 */
export const invalidateProgress = async (client: QueryClient): Promise<void> => {
  await Promise.all([
    client.invalidateQueries({ queryKey: SETUP_STATUS_QUERY_KEY_PREFIX }),
    client.invalidateQueries({ queryKey: QUICK_START_QUERY_KEY }),
  ]);
};

/**
 * The one client of the app. Refine gets it through `options.reactQuery.clientConfig`, so
 * Refine's own data hooks and every custom query share it.
 */
export const createQueryClient = (): QueryClient =>
  new QueryClient({
    // Not awaited: a save must not wait for the refetch.
    mutationCache: new MutationCache({
      onSuccess: (_data, _variables, _onMutateResult, _mutation, { client }) => {
        void invalidateProgress(client);
      },
    }),
    defaultOptions: {
      queries: {
        // Refine's defaults, which it applies only to a client that it builds itself.
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
        retry: (failureCount, error) => {
          // Don't retry on 404 (endpoint doesn't exist)
          if (axios.isAxiosError(error) && error.response?.status === 404) {
            return false;
          }
          // Retry up to 2 times for other errors
          return failureCount < 2;
        },
      },
    },
  });
